import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Card, CardContent } from "@/components/ui/card";
import { 
  ArrowLeft, 
  Shield, 
  Lock, 
  Server, 
  Eye, 
  Database,
  Key,
  Trash2,
  Globe,
  UserCheck,
  HelpCircle,
  Mail
} from "lucide-react";
import { useNavigate, Link } from "react-router-dom";

const faqCategories = [
  {
    title: "Chiffrement & Protection",
    icon: Lock,
    color: "text-emerald-600",
    bgColor: "bg-emerald-500/10",
    questions: [
      {
        question: "Comment mes documents sont-ils protégés ?",
        answer: "Vos documents sont chiffrés avec l'algorithme AES-256, le même standard utilisé par les banques et les gouvernements. Le chiffrement s'applique à la fois au repos (stockage) et en transit (transfert). Même nos équipes techniques ne peuvent pas accéder au contenu de vos fichiers."
      },
      {
        question: "Qu'est-ce que le chiffrement AES-256 ?",
        answer: "AES-256 est un algorithme de chiffrement de grade militaire. Le '256' fait référence à la taille de la clé de chiffrement (256 bits), ce qui rend le déchiffrement par force brute pratiquement impossible. Il faudrait des milliards d'années à un superordinateur pour casser ce chiffrement."
      },
      {
        question: "Mes données sont-elles chiffrées pendant le transfert ?",
        answer: "Oui, toutes les communications entre votre appareil et nos serveurs utilisent le protocole TLS 1.3 (la version la plus récente de HTTPS). Cela garantit que personne ne peut intercepter vos données pendant leur transfert."
      }
    ]
  },
  {
    title: "Hébergement & Infrastructure",
    icon: Server,
    color: "text-blue-600",
    bgColor: "bg-blue-500/10",
    questions: [
      {
        question: "Où sont stockées mes données ?",
        answer: "Vos données sont hébergées sur des serveurs situés en France et en Europe, conformément au RGPD. Notre infrastructure est fournie par Supabase, qui utilise des datacenters certifiés ISO 27001 avec surveillance 24/7."
      },
      {
        question: "Qui a accès à mes documents ?",
        answer: "Vous seul avez accès à vos documents. Nos employés n'ont pas accès aux contenus de vos fichiers grâce au chiffrement. Seules les métadonnées techniques (taille du fichier, date d'upload) peuvent être consultées pour le support technique, et uniquement sur demande explicite."
      },
      {
        question: "Que se passe-t-il en cas de panne des serveurs ?",
        answer: "Nos serveurs disposent de sauvegardes automatiques quotidiennes et de réplication géographique. En cas de panne, vos données sont récupérables à partir des sauvegardes. Notre objectif de temps de récupération (RTO) est de moins de 4 heures."
      }
    ]
  },
  {
    title: "Intelligence Artificielle",
    icon: Eye,
    color: "text-purple-600",
    bgColor: "bg-purple-500/10",
    questions: [
      {
        question: "L'IA lit-elle mes documents ?",
        answer: "L'IA analyse vos documents uniquement pour les classer et extraire les informations utiles (dates, montants, entités). Cette analyse est effectuée de manière sécurisée et les données ne sont jamais utilisées pour entraîner des modèles. Seul le résultat de l'analyse (titre, catégorie) est conservé."
      },
      {
        question: "Mes documents sont-ils utilisés pour entraîner l'IA ?",
        answer: "Non, absolument pas. Vos documents ne sont jamais utilisés pour entraîner ou améliorer des modèles d'intelligence artificielle. Nous utilisons des modèles pré-entraînés qui traitent vos données de manière éphémère sans les conserver."
      },
      {
        question: "Quels fournisseurs d'IA utilisez-vous ?",
        answer: "Nous utilisons Google Cloud AI (Gemini) pour l'analyse documentaire. Google s'engage contractuellement à ne pas conserver ni utiliser vos données pour l'entraînement de leurs modèles, conformément à leurs engagements RGPD."
      }
    ]
  },
  {
    title: "Vos Droits RGPD",
    icon: UserCheck,
    color: "text-orange-600",
    bgColor: "bg-orange-500/10",
    questions: [
      {
        question: "Comment puis-je exporter toutes mes données ?",
        answer: "Dans les Paramètres > Export des données, vous pouvez télécharger l'intégralité de vos documents et métadonnées dans un fichier ZIP. L'export inclut tous vos fichiers originaux ainsi qu'un fichier JSON/CSV avec les métadonnées."
      },
      {
        question: "Comment supprimer mon compte et mes données ?",
        answer: "Vous pouvez supprimer votre compte dans Paramètres > Compte > Supprimer mon compte. Cette action supprime définitivement tous vos documents et données personnelles dans un délai de 30 jours. Cette action est irréversible."
      },
      {
        question: "Combien de temps conservez-vous mes données ?",
        answer: "Vos documents sont conservés tant que votre compte est actif. Après suppression du compte, toutes les données sont effacées sous 30 jours. Les logs de connexion sont anonymisés après 30 jours et supprimés après 1 an. Les données de facturation sont conservées 10 ans conformément à la loi."
      },
      {
        question: "Puis-je demander la rectification de mes données ?",
        answer: "Oui, vous pouvez modifier vos informations personnelles directement dans les Paramètres. Pour toute demande spécifique, contactez-nous à privacy@easyfinder.fr et nous traiterons votre demande sous 30 jours."
      }
    ]
  },
  {
    title: "Partage & Collaboration",
    icon: Globe,
    color: "text-indigo-600",
    bgColor: "bg-indigo-500/10",
    questions: [
      {
        question: "Que se passe-t-il quand je partage un document ?",
        answer: "Quand vous partagez un document, un lien unique et sécurisé est généré. Seules les personnes ayant ce lien peuvent accéder au document. Vous pouvez définir des permissions (lecture seule ou commentaires) et une date d'expiration."
      },
      {
        question: "Les liens de partage sont-ils sécurisés ?",
        answer: "Oui, chaque lien de partage contient un token unique généré aléatoirement (128 bits d'entropie). Ce token est pratiquement impossible à deviner. Vous pouvez également protéger le lien avec un mot de passe et définir une date d'expiration."
      },
      {
        question: "Puis-je révoquer un lien de partage ?",
        answer: "Oui, vous pouvez supprimer un lien de partage à tout moment depuis la page Partages. Une fois supprimé, le lien devient immédiatement invalide et le document n'est plus accessible aux destinataires."
      }
    ]
  },
  {
    title: "Conformité & Certifications",
    icon: Shield,
    color: "text-rose-600",
    bgColor: "bg-rose-500/10",
    questions: [
      {
        question: "EasyFinder est-il conforme au RGPD ?",
        answer: "Oui, EasyFinder est entièrement conforme au Règlement Général sur la Protection des Données (RGPD). Nous sommes basés en France, nos serveurs sont en Europe, et nous respectons tous vos droits (accès, rectification, effacement, portabilité, opposition)."
      },
      {
        question: "Quelles certifications avez-vous ?",
        answer: "Notre infrastructure est hébergée sur des serveurs certifiés ISO 27001 (sécurité de l'information) et SOC 2 Type II. Nos partenaires de paiement (Stripe) sont certifiés PCI-DSS pour la sécurité des transactions."
      },
      {
        question: "Avez-vous un DPO (Délégué à la Protection des Données) ?",
        answer: "Oui, vous pouvez contacter notre DPO pour toute question relative à la protection de vos données personnelles à l'adresse privacy@easyfinder.fr."
      }
    ]
  }
];

export default function SecurityFAQ() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-background/80 border-b border-border">
        <div className="container mx-auto px-4 h-16 flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-2">
            <HelpCircle className="w-5 h-5 text-primary" />
            <h1 className="text-xl font-semibold">FAQ Sécurité & Confidentialité</h1>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Hero Section */}
        <div className="text-center mb-10">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center">
            <Shield className="w-8 h-8 text-primary" />
          </div>
          <h2 className="text-2xl font-bold mb-2">Questions fréquentes sur la sécurité</h2>
          <p className="text-muted-foreground max-w-xl mx-auto">
            Nous prenons la protection de vos données très au sérieux. 
            Trouvez ici les réponses à vos questions sur notre politique de sécurité.
          </p>
        </div>

        {/* Trust Badges */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-10">
          <Card className="border-emerald-500/20">
            <CardContent className="p-3 text-center">
              <Lock className="w-5 h-5 mx-auto mb-1 text-emerald-600" />
              <p className="text-xs font-medium">AES-256</p>
            </CardContent>
          </Card>
          <Card className="border-blue-500/20">
            <CardContent className="p-3 text-center">
              <Globe className="w-5 h-5 mx-auto mb-1 text-blue-600" />
              <p className="text-xs font-medium">Serveurs EU</p>
            </CardContent>
          </Card>
          <Card className="border-purple-500/20">
            <CardContent className="p-3 text-center">
              <Shield className="w-5 h-5 mx-auto mb-1 text-purple-600" />
              <p className="text-xs font-medium">RGPD</p>
            </CardContent>
          </Card>
          <Card className="border-orange-500/20">
            <CardContent className="p-3 text-center">
              <Key className="w-5 h-5 mx-auto mb-1 text-orange-600" />
              <p className="text-xs font-medium">ISO 27001</p>
            </CardContent>
          </Card>
        </div>

        {/* FAQ Categories */}
        <div className="space-y-8">
          {faqCategories.map((category, categoryIndex) => (
            <div key={categoryIndex}>
              <div className="flex items-center gap-3 mb-4">
                <div className={`w-10 h-10 rounded-xl ${category.bgColor} flex items-center justify-center`}>
                  <category.icon className={`w-5 h-5 ${category.color}`} />
                </div>
                <h3 className="text-lg font-semibold">{category.title}</h3>
              </div>
              
              <Accordion type="single" collapsible className="space-y-2">
                {category.questions.map((faq, faqIndex) => (
                  <AccordionItem 
                    key={faqIndex} 
                    value={`${categoryIndex}-${faqIndex}`}
                    className="border border-border rounded-xl px-4 data-[state=open]:bg-muted/30"
                  >
                    <AccordionTrigger className="text-sm font-medium text-left hover:no-underline py-4">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-muted-foreground pb-4 leading-relaxed">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          ))}
        </div>

        {/* Contact Section */}
        <Card className="mt-10 border-primary/20 bg-primary/5">
          <CardContent className="p-6 text-center">
            <Mail className="w-8 h-8 mx-auto mb-3 text-primary" />
            <h3 className="font-semibold mb-2">Une question non répondue ?</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Notre équipe est disponible pour répondre à toutes vos questions sur la sécurité.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a 
                href="mailto:privacy@easyfinder.fr" 
                className="text-primary hover:underline text-sm font-medium"
              >
                privacy@easyfinder.fr
              </a>
              <span className="hidden sm:inline text-muted-foreground">•</span>
              <Link 
                to="/privacy" 
                className="text-primary hover:underline text-sm font-medium"
              >
                Politique de confidentialité complète
              </Link>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
